import { useState } from "react";
import type { previewUserDataDeletion } from "../../../../../../src/utils/admin_user_data_deletion";
import { DashboardUtils } from "../../../dashboard_utils";
import { trpc } from "../../../utils/trpc";
import { SeshPrimaryButton } from "../base_components/sesh_primary_button";
import { SeshTextInput } from "../base_components/sesh_text_input";
import { SeshWarningButton } from "../base_components/sesh_warning_button";

type Preview = Awaited<ReturnType<typeof previewUserDataDeletion>>;

export function AdminDeleteUserData() {
	const [userId, setUserId] = useState("");
	// The preview along with the exact user ID it was fetched for. Deleting is
	// only allowed while the input still matches, so a stale preview can never
	// be confirmed against.
	const [preview, setPreview] = useState<{
		userId: string;
		data: Preview;
	} | null>(null);
	const [showConfirm, setShowConfirm] = useState(false);
	const [confirmText, setConfirmText] = useState("");
	const [result, setResult] = useState<
		{ label: string; count: number }[] | null
	>(null);

	const trimmedUserId = userId.trim();
	const isValidId = /^\d+$/.test(trimmedUserId);
	const previewMatchesInput =
		preview !== null && preview.userId === trimmedUserId;

	const previewMutation = trpc.admin.previewDeleteUserData.useMutation();
	const deleteMutation = trpc.admin.deleteUserData.useMutation({
		onSuccess: (data) => {
			setShowConfirm(false);
			setConfirmText("");
			setPreview(null);
			setResult(data.deleted.filter((row) => row.count > 0));
		},
	});

	async function runPreview() {
		setResult(null);
		setPreview(null);
		try {
			const data = await previewMutation.mutateAsync({
				user_id: trimmedUserId,
			});
			setPreview({ userId: trimmedUserId, data });
		} catch {
			// Error surfaced via previewMutation.isError below.
		}
	}

	const totalToDelete =
		preview?.data.deleted.reduce((sum, row) => sum + row.count, 0) ?? 0;

	return (
		<div className="flex max-w-2xl flex-col gap-6">
			<div className="text-slate-300">
				Removes a user's personal sesh data for a deletion request:
				RSVPs, votes, reminders, calendar syncs/links, timefinder
				responses, and personal settings. Guild-owned content they
				created (events, polls, templates, integrations), guild logs,
				and billing records are <span className="font-bold">kept</span>{" "}
				so nothing other people rely on breaks.
			</div>

			<div className="flex flex-col gap-2">
				<div className="font-bold">Discord User ID</div>
				<SeshTextInput
					input_props={{
						value: userId,
						onChange: (e) => {
							setUserId(e.currentTarget.value);
							setPreview(null);
							setResult(null);
						},
						placeholder: "e.g. 1466252954964332666",
					}}
				/>
			</div>

			<div className="flex gap-2">
				<SeshPrimaryButton
					className="!w-auto px-4"
					is_loading={previewMutation.isLoading}
					disabled={!isValidId}
					onClick={runPreview}
				>
					Preview Deletion
				</SeshPrimaryButton>
			</div>

			{previewMutation.isError && (
				<div className="text-red-400">
					Error: {previewMutation.error.message}
				</div>
			)}

			{previewMatchesInput && (
				<div className="flex flex-col gap-4">
					{!preview.data.user_exists && (
						<div className="rounded-lg border border-yellow-700 bg-yellow-900/30 p-3 text-sm text-yellow-300">
							No user record exists for this ID. Any rows below
							still reference the ID and can be removed.
						</div>
					)}

					<div className="flex flex-col gap-3 rounded-lg border border-red-800 bg-gray-800 p-4">
						<div className="font-bold text-red-300">
							Will be deleted
						</div>
						<div className="flex flex-col gap-2">
							{preview.data.deleted.map((row) => (
								<div
									key={row.key}
									className={`flex justify-between gap-4 text-sm ${
										row.count === 0 ? "opacity-40" : ""
									}`}
								>
									<div className="flex flex-col">
										<span className="text-slate-200">
											{row.label}
										</span>
										<span className="text-xs text-slate-400">
											{row.description}
										</span>
									</div>
									<span className="font-mono text-slate-200">
										{row.count}
									</span>
								</div>
							))}
						</div>
						{preview.data.scrubbed_user_fields.length > 0 && (
							<div className="flex flex-col gap-1 text-sm">
								<div className="font-semibold text-slate-300">
									User settings that will be reset:
								</div>
								<ul className="list-inside list-disc text-slate-400">
									{preview.data.scrubbed_user_fields.map(
										(field) => (
											<li key={field}>{field}</li>
										)
									)}
								</ul>
							</div>
						)}
					</div>

					<div className="flex flex-col gap-3 rounded-lg border border-gray-600 bg-gray-800 p-4">
						<div className="font-bold text-slate-300">
							Will be kept
						</div>
						<div className="flex flex-col gap-2">
							{preview.data.retained.map((row) => (
								<div
									key={row.key}
									className={`flex justify-between gap-4 text-sm ${
										row.count === 0 ? "opacity-40" : ""
									}`}
								>
									<div className="flex flex-col">
										<span className="text-slate-200">
											{row.label}
										</span>
										<span className="text-xs text-slate-400">
											{row.reason}
										</span>
									</div>
									<span className="font-mono text-slate-200">
										{row.count}
									</span>
								</div>
							))}
						</div>
					</div>

					{totalToDelete > 0 ? (
						<div className="flex gap-2">
							<SeshWarningButton
								className="!w-auto px-4"
								onClick={() => setShowConfirm(true)}
							>
								Delete User Data
							</SeshWarningButton>
						</div>
					) : (
						<div className="text-slate-400">
							Nothing to delete for this user.
						</div>
					)}
				</div>
			)}

			{result && (
				<div className="flex flex-col gap-2 rounded-lg border border-green-700 bg-green-900/30 p-4 text-green-300">
					<div className="font-bold">User data deleted.</div>
					{result.map((row) => (
						<div
							key={row.label}
							className="flex justify-between text-sm"
						>
							<span>{row.label}</span>
							<span className="font-mono">{row.count}</span>
						</div>
					))}
				</div>
			)}

			{deleteMutation.isError && (
				<div className="text-red-400">
					Error: {deleteMutation.error.message}. Deletion is safe to
					re-run — preview again to see what's left.
				</div>
			)}

			<DashboardUtils.SeshModal
				is_active={showConfirm}
				onClose={() => {
					setShowConfirm(false);
					setConfirmText("");
				}}
				title="Confirm user data deletion"
				content={
					<div className="flex flex-col gap-4">
						<p className="text-slate-300">
							This permanently deletes the data listed in the
							preview for user{" "}
							<span className="font-mono">{trimmedUserId}</span>.
							This cannot be undone.
						</p>
						<div className="flex flex-col gap-2">
							<div className="text-sm text-slate-400">
								Type the user ID to confirm:
							</div>
							<SeshTextInput
								input_props={{
									value: confirmText,
									onChange: (e) =>
										setConfirmText(e.currentTarget.value),
									placeholder: trimmedUserId,
								}}
							/>
						</div>
						<div className="flex justify-end gap-2">
							<SeshPrimaryButton
								className="!w-auto bg-slate-600 px-4 hover:bg-slate-700"
								onClick={() => {
									setShowConfirm(false);
									setConfirmText("");
								}}
							>
								Cancel
							</SeshPrimaryButton>
							<SeshWarningButton
								className="!w-auto px-4 disabled:cursor-not-allowed disabled:opacity-50"
								is_loading={deleteMutation.isLoading}
								disabled={confirmText.trim() !== trimmedUserId}
								onClick={() =>
									deleteMutation.mutate({
										user_id: trimmedUserId,
									})
								}
							>
								Delete User Data
							</SeshWarningButton>
						</div>
					</div>
				}
			/>
		</div>
	);
}
